// CHANGE-024 shared bits: rows from the staff API shown as tables where every id is a link, money columns read as
// dollars (the cents stay in the tooltip), and one history list per thing.
// CHANGE-070: the staff app's tables and dates (kit/time), same sections, filters and links.
import { useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { cents } from '../../lib'
import { Button } from './kit'
import { dayOf, when as kitWhen } from './kit/time'

export type Row = Record<string, unknown>
export type Section = { key?: string; title: string; rows: Row[]; note?: string }
export type Fact = { label: string; value: unknown; link?: string }
export type HistoryItem = { source: 'event' | 'audit' | 'job' | 'mail'; at: string; what: string; id: string; who?: string; who_id?: string; detail?: unknown }
export type Linked = { table: string; column: string; rows: Row[] }

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const ISO = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2}(\.\d+)?)?([+-]\d{2}:\d{2}|Z)?)?$/

const PEOPLE = new Set(['user_id', 'accepted_by', 'actor_user_id', 'added_by', 'approved_by', 'assigned_to', 'assignee',
  'claimant_user_id', 'confirmed_by', 'connected_by', 'created_by', 'decided_by', 'ended_by', 'invited_by',
  'named_by_user_id', 'published_by', 'recorded_by', 'requested_by', 'reported_by', 'reviewed_by', 'second_actor_id',
  'sent_by', 'started_by', 'updated_by', 'uploaded_by', 'staff_user_id', 'who_id'])
const PAGE_OF: Record<string, string> = {
  company_id: 'company', claimant_company_id: 'company', target_company_id: 'company',
  partner_id: 'partner', charge_id: 'charge', redemption_id: 'payout', run_id: 'bill', billing_run_id: 'bill',
  calculation_id: 'calc', previous_calculation_id: 'calc', file_id: 'file', batch_id: 'file',
}

/** Where a uuid in this column opens: its own page when the column says what it is, else /staff/go/<id>. */
export function pathFor(column: string, id: string): string {
  if (PEOPLE.has(column)) return `/staff/person/${id}`
  const kind = PAGE_OF[column]
  return kind ? `/staff/${kind}/${id}` : `/staff/go/${id}`
}

export function IdLink({ id, column = 'id', children }: { id: string; column?: string; children?: ReactNode }) {
  return (
    <Link to={pathFor(column, id)} className="st-link st-mono" title={id} data-testid="staff-id-link">
      {children ?? id.slice(0, 8)}
    </Link>
  )
}

/** A date alone reads "Nov 2"; a time reads "Nov 2, 3:14 PM". Anything else comes back as it was. */
export function when(iso: string): string {
  if (Number.isNaN(new Date(iso).getTime())) return iso
  return iso.length <= 10 ? dayOf(iso) : kitWhen(iso)
}

/** One value as staff should read it. */
export function Cell({ column, value }: { column: string; value: unknown }) {
  if (value === null || value === undefined || value === '') return <span className="st-faint">–</span>
  if (typeof value === 'boolean') return <>{value ? 'yes' : 'no'}</>
  if (typeof value === 'number' && /(_cents| \(cents\))$/.test(column)) return <span title={`${value} cents`}>{cents(value)}</span>
  if (typeof value === 'number') return <>{value.toLocaleString()}</>
  if (typeof value === 'string' && UUID.test(value)) return <IdLink id={value} column={column} />
  if (typeof value === 'string' && /^\/staff\//.test(value)) return <Link className="st-link" to={value}>open</Link>
  if (typeof value === 'string' && ISO.test(value)) return <span title={value}>{when(value)}</span>
  if (typeof value === 'object') return <Json value={value} />
  return <>{String(value)}</>
}

export function Json({ value }: { value: unknown }) {
  const text = JSON.stringify(value, null, 2)
  if (text.length < 80) return <code className="st-json-inline">{JSON.stringify(value)}</code>
  return (
    <details className="st-json">
      <summary className="st-mono">{JSON.stringify(value).slice(0, 60)}…</summary>
      <pre>{text}</pre>
    </details>
  )
}

const label = (k: string) => k.replace(/_/g, ' ')

export function RowsTable({ rows, testid, anchor }: { rows: Row[]; testid?: string; anchor?: (r: Row) => string | undefined }) {
  if (!rows.length) return <p className="st-note">None</p>
  const cols = Array.from(rows.reduce((s, r) => { Object.keys(r).forEach((k) => s.add(k)); return s }, new Set<string>()))
  return (
    <div className="st-table-wrap">
      <table className="st-table stack st-eye-table" data-testid={testid}>
        <thead><tr>{cols.map((c) => <th key={c}>{label(c)}</th>)}</tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} id={anchor?.(r)}>
              {cols.map((c) => <td key={c} data-label={label(c)} className={r[c] === null || r[c] === undefined || r[c] === '' ? 'st-empty' : typeof r[c] === 'number' ? 'st-num' : undefined}><Cell column={c} value={r[c]} /></td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export function SectionBlock({ s, anchor }: { s: Section; anchor?: (r: Row) => string | undefined }) {
  return (
    <section className="st-section" id={s.key} data-testid={`staff-section-${s.key ?? s.title}`}>
      <div className="st-section-head"><h2 className="st-section-title">{s.title}<span>{s.rows.length}</span></h2></div>
      {s.note ? <p className="st-note">{s.note}</p> : null}
      <RowsTable rows={s.rows} anchor={anchor} />
    </section>
  )
}

export function FactList({ facts }: { facts: Fact[] }) {
  return (
    <dl className="st-facts st-eye-facts" data-testid="staff-facts">
      {facts.map((f) => (
        <div key={f.label}>
          <dt>{f.label.replace(/ \(cents\)$/, '')}</dt>
          <dd>{f.link && f.value !== null && f.value !== undefined ? <Link className="st-link" to={f.link}>{String(f.value)}</Link> : <Cell column={f.label} value={f.value} />}</dd>
        </div>
      ))}
    </dl>
  )
}

const SOURCE: Record<HistoryItem['source'], string> = { event: 'Event', audit: 'Staff or user action', job: 'Job', mail: 'Mail' }

const PHONE_HISTORY = 50     // past this a phone page runs for screens, so the rest waits behind Show all

export function HistoryList({ items }: { items: HistoryItem[] }) {
  const [source, setSource] = useState<string>('')
  const [all, setAll] = useState(false)
  const shown = source ? items.filter((h) => h.source === source) : items
  const cut = !all && shown.length > PHONE_HISTORY && window.matchMedia('(max-width: 860px)').matches
  return (
    <section className="st-section" id="history" data-testid="staff-history">
      <div className="st-section-head">
        <h2 className="st-section-title">History<span>{items.length}</span></h2>
        <select className="st-select" value={source} onChange={(e) => setSource(e.target.value)} aria-label="Show" data-testid="staff-history-filter">
          <option value="">Everything</option>
          {Object.entries(SOURCE).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>
      {shown.length === 0 ? <p className="st-note">Nothing yet</p> : (
        <ol className="st-tl st-eye-history">
          {(cut ? shown.slice(0, PHONE_HISTORY) : shown).map((h) => (
            <li key={`${h.source}-${h.id}`} className="st-tl-row" data-testid="staff-history-item">
              <span className="st-tl-when" title={h.at}>{when(h.at)}</span>
              <span>
                <span className="st-strong">{h.what}</span>
                <span className="st-tl-sub"> {SOURCE[h.source].toLowerCase()}</span>
                {h.who ? <span className="st-tl-sub"> by {h.who_id ? <IdLink id={h.who_id} column="who_id">{h.who}</IdLink> : h.who}</span> : null}
                {h.detail !== undefined && h.detail !== null ? <div><Json value={h.detail} /></div> : null}
              </span>
            </li>
          ))}
        </ol>
      )}
      {cut ? <div><Button tone="quiet" onClick={() => setAll(true)} data-testid="staff-history-all">Show all {shown.length}</Button></div> : null}
    </section>
  )
}

export function LinkedList({ linked }: { linked: Linked[] }) {
  if (!linked.length) return null
  return (
    <section className="st-section" id="linked" data-testid="staff-linked">
      <div className="st-section-head"><h2 className="st-section-title">Everything linked</h2><span className="st-note">Every row in any table that points here</span></div>
      {linked.map((l) => (
        <details key={`${l.table}.${l.column}`} className="st-linked">
          <summary><span className="st-mono">{l.table}</span> <span className="st-muted">by {l.column}</span> <span className="st-muted">{l.rows.length}</span></summary>
          <RowsTable rows={l.rows} />
        </details>
      ))}
    </section>
  )
}
